<template>
<div class="cart-page">
    <div class="cart-page__title-wrapper">
        <div class="flex-column">
            <h1 class="headline-xl">{{ listName }}</h1>
            <p v-if="list.created_at && user.is_dealer" class="paragraph-feature list-date">{{ list.created_at }}</p>
        </div>
        <div class="cart-info">
            <p v-if="list.created_at && user.is_dealer" class="paragraph-feature list-date-mobile">{{ list.created_at }}</p>
            <p class="paragraph-feature">{{ list.total_quantity }} Item(s)<span class="mobile-only" v-if="canSeePricing">{{ currencySymbol }}{{ subTotal }}</span></p>
        </div>
    </div>
    <div v-if="list.total_skus">
        <div class="cart-page__two-columns">
            <div class="first-column">
                <div class="cart-page__featured-message-wrapper" v-if="isPropertyVisible('free_shipping_msg')">
                    <div class="cart-page__featured-message shipping-info" :style="shippingInfoStyle">
                        <FreeShipping class="shipping-icon" alt="Free shipping"/>
                        <p class="title-xs">{{ freeShippingMessage }}</p>
                    </div>

                </div>

                <div class="cart-page__featured-message-wrapper" v-if="isPropertyVisible('b2b_cart_msg')">
                <div class="cart-page__featured-message">
                        <p class="paragraph-details">The contents of your cart are automatically saved to the Lists section of your account when logged in.</p>
                    </div>
                </div>

                <VirtualScroller class="products_row product_row cart_product_row">
                    <ListItem
                        v-for="(listItem, index) in computedListItems"
                        :key="listItem.list_item_id ?? `item-${index}`"
                        :list_item="listItem"
                        :is-restricted="isItemRestricted(listItem)"
                        :only="cartReloadProps"
                        @updateItemQuantity="updateItemQuantity(index, $event)"
                        @listItemError="handleListItemErrors(index, $event)"
                    />
                    <ListItem
                        v-for="(couponItem, index) in couponItems"
                        :key="`coupon-${couponItem.itemVariationId ?? index}`"
                        class="free-item"
                        :list_item="couponItem"
                        :free-item="couponItem.price <= 0"
                        :disable-edit-quantity="true"
                        :disable-delete-item="true"
                        :only="cartReloadProps"
                        readonly
                    />
                </VirtualScroller>
    
                <div class="cart-page__cart-actions">
                    <a :href="route('cart.print')" class="print-link" aria-label="Print your cart">
                        <PrintIcon alt="Print" /> Print Cart
                    </a>
                    <a aria-label="Clear your cart" @click="openListDelete()">
                        <TrashIcon alt="Clear Cart" />Clear Cart
                    </a>
                </div>
            </div>
    
            <div v-if="canSeePricing" class="second-column">
                <aside class="summary-content">
                    <CartSummary 
                        :list="list" 
                        :is-tax-exempt="isTaxExempt" 
                        :stripe-publishable-key="stripePublishableKey" 
                        :shipping-rates="shippingRates"
                        :has-restrictions="hasRestrictedItems"
                        v-model:restriction-context="restrictionContext"
                    />
                    <div class="summary-links">
                        <div class="links-wrapper">
                            <Link target="_blank" :href="user.is_dealer ? route('customer-service.cms', ['wholesale-shipping-information']) : route('index.cms', ['shipping-info-dtc']) " aria-label="View shipping information">Shipping Information</Link>
                            <Link v-if="!user.is_dealer" target="_blank" :href="route('customer-service.cms', ['consumer-return-policy'])" aria-label="View return policy">Return Policy</Link>
                        </div>
                        <Link target="_blank" :href="route('customer-service.index')" aria-label="Get help with your order">Need Help?</Link>
                    </div>
                </aside>
            </div>
        </div>
        <ProductCarousel :heading="{text: 'Recommended For You'}" :cart-products="cartProducts" boost-tag="view-cart" allow-quick-view/>
    </div>
    <div class="cart-page" v-else>
        <div class="cms-feature">
            <h4 class="headline-l results-header text-center"> No items in your cart</h4>
        </div>
        <ProductCarousel :heading="{text: 'Recommended For You'}" boost-tag="no-results" allow-quick-view/>

    </div>
</div>
</template>
<script lang="ts" setup>
    import { ref, computed, provide, reactive, inject, watch, onMounted, onBeforeUnmount } from "vue";
    import { useCartDetails } from "@/helpers/composables/useCartDetails";
    import ListItem from "@/components/cart/ListItem.vue";
    import CartSummary from "@/components/cart/CartSummary.vue";
    import ListDeleteModal from "@/components/ListDeleteModal.vue";
    import { Link, router } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { useDialog } from 'primevue/usedialog';
    import ProductCarousel from "@/components/ProductCarousel.vue";
    import FreeShipping from "@icons/free-shipping.svg";
    import PrintIcon from "@icons/print-icon.svg";
    import TrashIcon from "@icons/red-trash-icon.svg";
    import VirtualScroller from "@/components/VirtualScroller.vue";
    import { useCheckoutD2CStore } from "@/store/checkoutD2CStore";
    import { getMainLineCouponItem } from "@/helpers/composables/useCouponPseudoLine";
    const dialog = useDialog();
    let route = inject("route");
    interface List {
        list_id: number;
        name?: string;
        total?: number;
        amonut?: number;
    }

    let { list, list_items } = defineProps({
        list: {
            type: Object as PropType<List>,
            default: {}
        },
        list_items: {
            type: Array<any>,
            default: {}
        },
        isTaxExempt: {
            type: Boolean,
            default: false
        },
        stripePublishableKey: {
            type: String,
            default: null
        },
        shippingRates: {
            type: Object,
            default: {}
        }
    });

    const itemList = computed(() => {
        return list_items;
    });

    const listName = computed(()=>{
        return (user.value.is_dealer && list.name) ? list.name : "Your Cart";
    })
    
    let openListDelete = () => {
        dialog.open(ListDeleteModal, {
            props: {
                showHeader: false,
                modal: true
            },
            data: {
                clearCart: true,
                type:"list",
                list_id: list.list_id,
                list_item_id: '',
                beforeCloseForList(ctx) {
                    return new Promise((resolve) => {
                        router.reload();
                        resolve();
                    });
                }
            }
        });
    };

    const afterDelete = computed(() => {
        return route("cart.show");
    });

    let { isPropertyVisible, currencySymbol } = useCartDetails();

    let settingsStore = useSettingsStore();
    let { freeShippingMessage, freeShippingProgressPercentage, user, canSeePricing, canPlaceOrders } = storeToRefs(settingsStore);

    const shippingInfoStyle = computed(() =>  {
        return {
            '--shipping-progress': `${freeShippingProgressPercentage.value}%`
        };
    });

    let updateItemQuantity = function (index, $event: Event) {
        list_items[index].quantity = $event.value.quantity;
    };

    let subTotal = computed(() => {
        return parseFloat(list.total ?? 0).toFixed(2);
    });

    const listItemErrors = reactive({});

    provide('listItemErrors', listItemErrors);

    const handleListItemErrors = (index, $event) => {   
      if ($event.outOfStock) {
        listItemErrors[$event.index] = true;
      } else {
        delete listItemErrors[$event.index];
      }
    };

    // Restrictions (D2C)
    const { checkRestrictionsAwaitable } = useCheckoutD2CStore();
    const { restrictions, couponInformation } = storeToRefs(useCheckoutD2CStore());

    const cartReloadProps = ['list', 'list_items'];

    const couponItems = computed(() => {
        if (couponInformation.value?.applied !== true) {
            return [];
        }

        return couponInformation.value?.items
            ?.filter((item) => item.new === true)
            .map((item) => ({
                ...item,
                list_id: list.list_id,
                unit_price: item.priceWithoutDiscount,
                price: item.discountPrice,
                list_item_id: null,
                item_variation_id: item.itemVariationId,
                quantity: item.quantity,
                subtotal: +((+item.discountPrice || 0) * (+item.quantity || 0)).toFixed(2),
            })) ?? [];
    });


    const computedListItems = computed(() => {
        return (list_items ?? []).map((item) => {
            const couponItem = getMainLineCouponItem(
                couponInformation.value?.items,
                item.item_variation_id
            );
            if (!couponItem) {
                return item;
            }

            const applied = couponInformation.value?.applied;
            const price =
                applied && couponItem?.discountPrice != null
                    ? couponItem.discountPrice
                    : item.price;
            const quantity = applied ? (couponItem?.quantity || item.quantity) : item.quantity;

            return {
                ...item,
                price,
                quantity,
                subtotal: +(price * quantity).toFixed(2),
            };
        });
    });

    const restrictionContext = ref({
        region: null,
        postalCode: null,
        shippingMethod: null,
    });

    const restrictionPayload = computed(() => ({
        final_address: {
            country_alpha3_code: 'USA',
            region: restrictionContext.value?.region ?? null,
            postal_code: restrictionContext.value?.postalCode ?? null,
        },
        options: {
            country_alpha3_code: 'USA',
            region: restrictionContext.value?.region ?? null,
            postal_code: restrictionContext.value?.postalCode ?? null,
            customer: '800B2C',
            is_quote: false,
            shipping_method: restrictionContext.value?.shippingMethod ?? null,
        },
        order_skus: (list_items ?? []).map((item) => ({
            quantity: item.quantity,
            price: item.price,
            upc: item?.variation?.upc,
        })),
    }));

    watch([() => list_items, restrictionContext], async () => {
        if (user.value.is_dealer) return;
        if (!restrictionPayload.value?.order_skus?.length) return;
        try {
            await checkRestrictionsAwaitable(restrictionPayload.value);
        } catch (e) {}
    }, { immediate: true, deep: true });

    const restrictedUpcs = computed(() => {
        const skus = restrictions.value?.data?.skus;
        if (!Array.isArray(skus)) return new Set();
        return new Set(
            skus
                .filter((sku) => sku?.is_restricted)
                .map((sku) => sku?.upc)
                .filter(Boolean)
        );
    });

    const hasRestrictedItems = computed(() => {
        if (!restrictedUpcs.value.size) return false;
        return (list_items ?? []).some((item) => restrictedUpcs.value.has(item?.variation?.upc));
    });

    const isItemRestricted = (item) => {
        return restrictedUpcs.value.has(item?.variation?.upc);
    };


    let cartProducts = computed(()=>{
        return list_items.map((item)=>{
            return item.product_line_code
        })
    })

    function stopSentryReplay() {
        if (window.sentry) {
            const replay = window.sentry.getReplay();
            replay.stop();
        }
    }

    provide('stopCartPageSentryReplay', stopSentryReplay);

    const isGuestD2c = computed(
        () => !user.value.is_dealer && !user.value.user_id
    );

    onMounted(() => {
        if (window.sentry) {
            const replay = window.sentry.getReplay();
            replay.start();
        }
    });

    onBeforeUnmount(() => {
        if (isGuestD2c.value) {
            stopSentryReplay();
        }
    });
</script>

<style scoped> 
    .cart-page__featured-message-wrapper .shipping-info::after {
        width: var(--shipping-progress);
    }

</style>
